/**
 * THE RECENT SPEND RATE — how fast money is going out NOW, read off the dated
 * invoice ledger (29 Oct, Vinnie: a cost forecast "cannot be 200% unless it is
 * actually going into that direction based on spending"). This is the one new
 * ingredient the trend-aware EAC needs over the textbook BAC/CPI: the classic
 * estimate extrapolates the remaining work at the LIFETIME average cost-per-value,
 * which a single non-representative early spend can dominate forever. Reading the
 * burn over a TRAILING WINDOW instead projects the remaining work on where spending
 * is actually heading — if the burn has slowed the estimate eases, if it has
 * accelerated the estimate climbs.
 *
 * Pure: it takes the cost curve's cumulative PAID steps (src/budget-graph.ts — only
 * ever PAID, never committed, matching the EVM cost basis) and the workspace working
 * calendar, and returns a base-currency rate per WORKING day plus the evidence the
 * caller's history floor reads. No store, no React, no dates of its own beyond the
 * `now` it is handed. Unit-tested in scripts/test-cost-trend.ts.
 */
import { dayNum, type CostStep } from './budget-graph';
import { lineBaseAmount, type InvoiceLine, type BudgetContext } from './projects';
import { workingDaysInclusive, DEFAULT_CALENDAR, type WorkCalendar } from './work-calendar';

const DAY = 86_400_000;

/**
 * THE PAID-SPEND SERIES THE RATE READS — cumulative PAID, base currency, placed on the
 * day money ACTUALLY WENT OUT. A line's burn day is its `paidAt` instant (the immutable
 * "Mark paid" moment) when present, else its business `date`. This matters because the
 * recent-rate window and AC must agree on what "spent" means (29 Oct, monitoring-witness):
 * a line can carry a business `date` in the future (a post-dated invoice) or none at all
 * while already paid — reading `date` dropped it from the recent window yet AC still counted
 * it, so one invoice read as both "spent" and "not spent recently", flattering the EAC and
 * lurching when the clock passed the future date. Reading `paidAt` places the burn when it
 * truly happened. A paid line with NEITHER a paidAt nor a date cannot sit on a time axis, so
 * it stays in AC but out of the rate (untimed — honestly not attributable to recent burn).
 * Not the budget burn-up curve (src/budget-graph costCurve), which is a dated-invoice chart.
 */
export function paidSpendSteps(invoices: readonly InvoiceLine[], ctx?: BudgetContext): CostStep[] {
  const base = ctx?.base ?? '';
  const dated: { day: number; amt: number }[] = [];
  for (const l of invoices) {
    if (l.status !== 'paid') continue;
    const day = l.paidAt != null ? Math.floor(l.paidAt / DAY) : l.date ? dayNum(l.date) : null;
    if (day == null) continue; // untimed paid line — in AC, not placeable on the time axis
    const amt = Number.isFinite(l.amount) ? lineBaseAmount(l, base, ctx?.rates) : 0;
    dated.push({ day, amt });
  }
  dated.sort((a, b) => a.day - b.day);
  const steps: CostStep[] = [];
  let paid = 0;
  for (const { day, amt } of dated) {
    paid += amt;
    const last = steps[steps.length - 1];
    if (last && last.day === day) last.paid = paid;
    else steps.push({ day, date: '', paid, committed: 0 });
  }
  return steps;
}

/** A whole-day number (days since the epoch, UTC) back to a plain `YYYY-MM-DD` —
 *  the inverse of budget-graph's `dayNum`, so a cost step and the calendar share
 *  one day vocabulary. */
function dayToISO(day: number): string {
  return new Date(day * DAY).toISOString().slice(0, 10);
}

/** How many CALENDAR days back the trailing burn window reaches. Eight weeks is
 *  long enough to average over a lumpy invoicing month, short enough to still be
 *  "recent" rather than the lifetime average the classic EAC already is. */
export const SPEND_WINDOW_DAYS = 56;

/** The window must hold at least this many distinct dated PAID steps, and this many
 *  working days, before its rate is trustworthy enough to steer a forecast by — the
 *  spend-history floor (Vinnie, 29 Oct). Below it the caller falls back to the
 *  textbook BAC/CPI rather than reading a trend off one or two invoices. */
export const SPEND_FLOOR_STEPS = 2;
export const SPEND_FLOOR_WORKING_DAYS = 10;

/** A recent spend RATE is only a meaningful "where it's heading" signal when there is
 *  real spend BEFORE the window to compare it against — a prior baseline. Without one,
 *  the whole ledger sits inside the trailing window (a project that recorded nothing
 *  for weeks then booked it all in a burst — catch-up invoicing), and "spend ÷ recent
 *  days" reads artificially fast ONLY because the window is shorter than the elapsed
 *  project, not because spending accelerated. That inflates the EAC far past the
 *  honest BAC/CPI for no real reason (29 Oct, Core Systems read 615% over this way).
 *  So the trend is "representative" — safe to steer by — only when at least this
 *  fraction of all spend-to-date predates the window; otherwise the caller falls back
 *  to the lifetime BAC/CPI, which smooths the catch-up burst honestly. */
export const SPEND_BASELINE_MIN_FRACTION = 0.1;

export interface SpendTrend {
  /** Base-currency PAID per WORKING day across the trailing window. */
  perWorkingDay: number;
  /** Working days the window spans (>= 1), on the workspace calendar. */
  windowWorkingDays: number;
  /** PAID base-currency that accrued inside the window. */
  windowPaid: number;
  /** PAID base-currency that accrued BEFORE the window opened — the prior baseline a
   *  representative recent rate is compared against. */
  paidBefore: number;
  /** Distinct dated PAID steps inside the window — the history floor reads this. */
  stepsInWindow: number;
  /** True when the window clears {@link SPEND_FLOOR_STEPS}/{@link SPEND_FLOOR_WORKING_DAYS}
   *  AND actually spent something — i.e. a rate worth forecasting on. */
  readable: boolean;
  /** True when enough spend predates the window ({@link SPEND_BASELINE_MIN_FRACTION}) that
   *  the recent rate is a real "where it's heading" signal rather than a catch-up-invoicing
   *  artifact. A trend EAC should be used only when readable AND representative. */
  representative: boolean;
}

/** The cumulative PAID total as of a day-number — the last step on or before it,
 *  else 0 (nothing paid yet). Steps are ascending by day. */
function paidAsOf(steps: CostStep[], day: number): number {
  let paid = 0;
  for (const s of steps) {
    if (s.day > day) break;
    paid = s.paid;
  }
  return paid;
}

/**
 * The recent PAID spend rate, per working day, over the trailing
 * {@link SPEND_WINDOW_DAYS} ending at `now`. The window never starts before the
 * first dated spend, so an empty run-up before the project's first invoice is not
 * mistaken for a slow burn. `readable` says whether the floor is cleared.
 */
export function spendTrend(
  steps: CostStep[],
  now: number = Date.now(),
  cal: WorkCalendar = DEFAULT_CALENDAR,
): SpendTrend {
  const empty: SpendTrend = {
    perWorkingDay: 0,
    windowWorkingDays: 0,
    windowPaid: 0,
    paidBefore: 0,
    stepsInWindow: 0,
    readable: false,
    representative: false,
  };
  if (steps.length === 0) return empty;

  const nowDay = Math.floor(now / DAY);
  const firstDay = steps[0].day;
  // The window is the trailing SPEND_WINDOW_DAYS, clamped to open no earlier than
  // the first dated spend — before that there was no project to burn on.
  const windowStartDay = Math.max(firstDay, nowDay - SPEND_WINDOW_DAYS + 1);
  if (windowStartDay > nowDay) return empty;

  // Spend inside the window = cumulative at `now` minus cumulative the day BEFORE
  // the window opened (so a step landing on the opening day is counted in).
  const paidNow = paidAsOf(steps, nowDay);
  const paidBefore = paidAsOf(steps, windowStartDay - 1);
  const windowPaid = Math.max(0, paidNow - paidBefore);

  const stepsInWindow = steps.filter((s) => s.day >= windowStartDay && s.day <= nowDay).length;
  const windowWorkingDays = Math.max(
    1,
    workingDaysInclusive(dayToISO(windowStartDay), dayToISO(nowDay), cal),
  );
  const perWorkingDay = windowPaid / windowWorkingDays;
  const readable =
    windowPaid > 0 &&
    stepsInWindow >= SPEND_FLOOR_STEPS &&
    windowWorkingDays >= SPEND_FLOOR_WORKING_DAYS;
  // A prior baseline must carry a real share of spend, or the "recent rate" is just the
  // whole ledger compressed into a short window (catch-up invoicing) — see the constant.
  const paidToDate = paidBefore + windowPaid;
  const representative = paidToDate > 0 && paidBefore >= SPEND_BASELINE_MIN_FRACTION * paidToDate;

  return { perWorkingDay, windowWorkingDays, windowPaid, paidBefore, stepsInWindow, readable, representative };
}
